import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getMonthlyRpt } from "../../../../services/reportService";
import {
  showSpinner,
  closeSpinner,
  modal,
  alert,
} from "../../../../js/modules/Dialogs";
import { sessionExpired } from "../../../../config/messages";
import { toScreen } from "../../../../js/modules/Routing";
import PropTypes from "prop-types";
import withNavigate from "../../withNavigate";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";
import SearchFilterBar from "../../../commonUI/SearchFilterBar/SearchFilterBar";
import MonthlyTrendChart from "./MonthlyTrendChart";
import KPICardsGrid from "../../../commonUI/Cards/KPICardsGrid";
import {
  TrendingUpIcon,
  TrendingDownIcon,
} from "../../../commonUI/Icons/SvgIcon";
import TopNav from "../../../commonUI/TopHeader/TopHeader";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const RptMonthly = ({ userDetails }) => {
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const currentMonth = useMemo(() => new Date().getMonth(), []);
  const currentDay = useMemo(() => new Date().getDate(), []);
  const [reportYear, setReportYear] = useState(currentYear);
  const [compareYear, setCompareYear] = useState(currentYear - 1);
  const [focusMonth, setFocusMonth] = useState(currentMonth);
  const [reportData, setReportData] = useState([]);
  const [compareData, setCompareData] = useState([]);
  const navigate = useNavigate();

  const selectStyles = useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        minHeight: "40px",
        height: "40px",
        minWidth: "180px",
        width: "auto",
        padding: "0",
        boxShadow: "none",
        border: "1px solid #e0e0e0",
        "&:focus-visible": {
          borderColor: "#000000",
          boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #000000",
          outline: "none",
        },
      }),
      valueContainer: (provided) => ({
        ...provided,
        height: "40px",
        padding: "0 8px",
        display: "flex",
        alignItems: "center",
      }),
      input: (provided) => ({
        ...provided,
        margin: "0",
        padding: "0",
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: "40px",
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? "#f1f5f9"
          : state.isFocused
            ? "#f1f5f9"
            : null,
        color: "#333",
        "&:hover": {
          backgroundColor: "#f1f5f9",
        },
      }),
      indicatorSeparator: (provided) => ({
        ...provided,
        display: "none",
      }),
      placeholder: (provided) => ({
        ...provided,
        color: "#999",
      }),
    }),
    [],
  );

  const yearOptions = useMemo(() => {
    const options = [];
    for (let year = 2018; year <= currentYear; year++) {
      options.push({ value: year, label: year.toString() });
    }
    return options;
  }, [currentYear]);

  const monthOptions = useMemo(
    () => [
      { value: 0, label: "January" },
      { value: 1, label: "February" },
      { value: 2, label: "March" },
      { value: 3, label: "April" },
      { value: 4, label: "May" },
      { value: 5, label: "June" },
      { value: 6, label: "July" },
      { value: 7, label: "August" },
      { value: 8, label: "September" },
      { value: 9, label: "October" },
      { value: 10, label: "November" },
      { value: 11, label: "December" },
    ],
    [],
  );

  const getReports = useCallback(async () => {
    showSpinner();
    try {
      const [yearRes, compareRes] = await Promise.all([
        getMonthlyRpt({ userType: "mav", theYear: reportYear }),
        getMonthlyRpt({ userType: "mav", theYear: compareYear }),
      ]);
      closeSpinner();

      const filterAndDedup = (data, year) => {
        // If data has a year property, filter by it; otherwise trust the API returned the right year
        const hasYearProp = data.some((item) => item.year !== undefined);
        const filtered = hasYearProp
          ? data.filter((item) => item.year === year)
          : data;
        // Deduplicate by month — keep only the first occurrence of each month
        const seen = new Set();
        return filtered.filter((item) => {
          if (seen.has(item.month)) return false;
          seen.add(item.month);
          return true;
        });
      };

      setReportData(filterAndDedup(yearRes?.data || [], reportYear));
      setCompareData(filterAndDedup(compareRes?.data || [], compareYear));
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        alert("Error fetching report: " + error?.message);
      }
    }
  }, [reportYear, compareYear, navigate]);

  useEffect(() => {
    getReports();
  }, [getReports]);

  const focusMonthData = useMemo(() => {
    const rptMonth = reportData?.find((item) => item?.month === focusMonth + 1);
    const cmpMonth = compareData?.find(
      (item) => item?.month === focusMonth + 1,
    );
    return { report: rptMonth, compare: cmpMonth };
  }, [reportData, compareData, focusMonth]);

  const bannerInfo = useMemo(() => {
    const isCurrentMonth =
      focusMonth === currentMonth && reportYear === currentYear;
    if (!isCurrentMonth) return null;
    return {
      show: true,
      startDay: 1,
      endDay: currentDay,
      month: MONTHS[focusMonth],
      year: reportYear,
    };
  }, [focusMonth, reportYear, currentMonth, currentYear, currentDay]);

  const statCards = useMemo(() => {
    const rpt = focusMonthData.report || {};
    const cmp = focusMonthData.compare || {};

    const calcChange = (current, previous) => {
      if (!current || !previous) return null;
      return (((current - previous) / previous) * 100).toFixed(2);
    };

    return [
      {
        label: "Jobs posted",
        value: rpt.jobsPosted || rpt.jobCount || 0,
        prevValue: cmp.jobsPosted || cmp.jobCount || 0,
        change: calcChange(
          rpt.jobsPosted || rpt.jobCount,
          cmp.jobsPosted || cmp.jobCount,
        ),
        metric: "jobsPosted",
      },
      {
        label: "Quotes",
        value: rpt.quotes || rpt.totalQuotes || 0,
        prevValue: cmp.quotes || cmp.totalQuotes || 0,
        change: calcChange(
          rpt.quotes || rpt.totalQuotes,
          cmp.quotes || cmp.totalQuotes,
        ),
        metric: "quotes",
      },
      {
        label: "Bookings",
        value: rpt.bookings || rpt.booked || 0,
        prevValue: cmp.bookings || cmp.booked || 0,
        change: calcChange(
          rpt.bookings || rpt.booked,
          cmp.bookings || cmp.booked,
        ),
        metric: "bookings",
      },
      {
        label: "Booking conversion",
        value: rpt.conversionRate || rpt.bookedPercentage || 0,
        prevValue: cmp.conversionRate || cmp.bookedPercentage || 0,
        change: calcChange(
          rpt.conversionRate || rpt.bookedPercentage,
          cmp.conversionRate || cmp.bookedPercentage,
        ),
        metric: "conversionRate",
        isPercentage: true,
      },
    ];
  }, [focusMonthData]);

  const getMetricValue = (data, metric) => {
    switch (metric) {
      case "jobsPosted":
        return data.jobsPosted || data.jobCount || 0;
      case "unquotedJobs":
        return data.unquotedJobs || data.notQuoted || 0;
      case "bookings":
        return data.bookings || data.booked || 0;
      case "conversionRate":
        return data.conversionRate || data.bookedPercentage || 0;
      case "quotes":
        return data.quotes || data.totalQuotes || 0;
      case "clientRegistrations":
        return data.clientRegistrations || 0;
      case "driverRegistrations":
        return data.driverRegistrations || data.driverCount || 0;
      case "refunds":
        return data.refunds || 0;
      case "reviews":
        return data.reviews || 0;
      default:
        return 0;
    }
  };

  const tableRows = useMemo(() => {
    const rows = [];

    MONTHS.forEach((monthName, index) => {
      const rptData = reportData?.find((item) => item?.month === index + 1);
      const cmpData = compareData?.find((item) => item?.month === index + 1);

      const formatValue = (val) =>
        val !== null && val !== undefined ? val : "–";
      const formatDiff = (current, previous) => {
        if (!current || !previous) return null;
        const diff = ((current - previous) / previous) * 100;
        return diff.toFixed(2);
      };

      const jobDiff = formatDiff(
        rptData?.jobsPosted || rptData?.jobCount,
        cmpData?.jobsPosted || cmpData?.jobCount,
      );
      const unquotedDiff = formatDiff(
        rptData?.unquotedJobs || rptData?.notQuoted,
        cmpData?.unquotedJobs || cmpData?.notQuoted,
      );
      const bookingDiff = formatDiff(
        rptData?.bookings || rptData?.booked,
        cmpData?.bookings || cmpData?.booked,
      );
      const convDiff = formatDiff(
        rptData?.conversionRate || rptData?.bookedPercentage,
        cmpData?.conversionRate || cmpData?.bookedPercentage,
      );
      const quoteDiff = formatDiff(
        rptData?.quotes || rptData?.totalQuotes,
        cmpData?.quotes || cmpData?.totalQuotes,
      );
      const clientDiff = formatDiff(
        rptData?.clientRegistrations,
        cmpData?.clientRegistrations,
      );
      const driverDiff = formatDiff(
        rptData?.driverRegistrations || rptData?.driverCount,
        cmpData?.driverRegistrations || cmpData?.driverCount,
      );
      const refundsDiff = formatDiff(rptData?.refunds, cmpData?.refunds);
      const reviewsDiff = formatDiff(rptData?.reviews, cmpData?.reviews);

      // Report year row
      rows.push(
        <tr key={`${monthName}-${reportYear}`} className="table-report-year">
          <td className="month-cell">{monthName}</td>
          <td className="period-cell">{reportYear}</td>
          <td>{formatValue(rptData?.jobsPosted || rptData?.jobCount)}</td>
          <td>{formatValue(rptData?.unquotedJobs || rptData?.notQuoted)}</td>
          <td>{formatValue(rptData?.bookings || rptData?.booked)}</td>
          <td>
            {formatValue(
              (rptData?.conversionRate || rptData?.bookedPercentage)?.toFixed(
                2,
              ),
            )}
          </td>
          <td>{formatValue(rptData?.quotes || rptData?.totalQuotes)}</td>
          <td>{formatValue(rptData?.clientRegistrations)}</td>
          <td>
            {formatValue(rptData?.driverRegistrations || rptData?.driverCount)}
          </td>
          <td>{formatValue(rptData?.refunds)}</td>
          <td>{formatValue(rptData?.reviews)}</td>
        </tr>,
      );

      // Compare year row
      rows.push(
        <tr key={`${monthName}-${compareYear}`} className="table-compare-year">
          <td className="month-cell"></td>
          <td className="period-cell">{compareYear}</td>
          <td>{formatValue(cmpData?.jobsPosted || cmpData?.jobCount)}</td>
          <td>{formatValue(cmpData?.unquotedJobs || cmpData?.notQuoted)}</td>
          <td>{formatValue(cmpData?.bookings || cmpData?.booked)}</td>
          <td>
            {formatValue(
              (cmpData?.conversionRate || cmpData?.bookedPercentage)?.toFixed(
                2,
              ),
            )}
          </td>
          <td>{formatValue(cmpData?.quotes || cmpData?.totalQuotes)}</td>
          <td>{formatValue(cmpData?.clientRegistrations)}</td>
          <td>
            {formatValue(cmpData?.driverRegistrations || cmpData?.driverCount)}
          </td>
          <td>{formatValue(cmpData?.refunds)}</td>
          <td>{formatValue(cmpData?.reviews)}</td>
        </tr>,
      );

      // Difference row
      rows.push(
        <tr key={`${monthName}-diff`} className="difference-row">
          <td
            colSpan="2"
            style={{ position: "sticky", left: 0, zIndex: 9 }}
          ></td>
          <td className={jobDiff && jobDiff >= 0 ? "positive" : "negative"}>
            {jobDiff !== null ? (
              <>
                {jobDiff > 0 ? (
                  <TrendingUpIcon width="16" height="16" />
                ) : (
                  <TrendingDownIcon width="16" height="16" />
                )}
                {Math.abs(jobDiff)}%
              </>
            ) : (
              "–"
            )}
          </td>
          <td
            className={
              unquotedDiff && unquotedDiff >= 0 ? "positive" : "negative"
            }
          >
            {unquotedDiff !== null ? (
              <>
                {unquotedDiff > 0 ? (
                  <TrendingUpIcon width="16" height="16" />
                ) : (
                  <TrendingDownIcon width="16" height="16" />
                )}
                {Math.abs(unquotedDiff)}%
              </>
            ) : (
              "–"
            )}
          </td>
          <td
            className={
              bookingDiff && bookingDiff >= 0 ? "positive" : "negative"
            }
          >
            {bookingDiff !== null ? (
              <>
                {bookingDiff > 0 ? (
                  <TrendingUpIcon width="16" height="16" />
                ) : (
                  <TrendingDownIcon width="16" height="16" />
                )}
                {Math.abs(bookingDiff)}%
              </>
            ) : (
              "–"
            )}
          </td>
          <td className={convDiff && convDiff >= 0 ? "positive" : "negative"}>
            {convDiff !== null ? (
              <>
                {convDiff > 0 ? (
                  <TrendingUpIcon width="16" height="16" />
                ) : (
                  <TrendingDownIcon width="16" height="16" />
                )}
                {Math.abs(convDiff)}%
              </>
            ) : (
              "–"
            )}
          </td>
          <td className={quoteDiff && quoteDiff >= 0 ? "positive" : "negative"}>
            {quoteDiff !== null ? (
              <>
                {quoteDiff > 0 ? (
                  <TrendingUpIcon width="16" height="16" />
                ) : (
                  <TrendingDownIcon width="16" height="16" />
                )}
                {Math.abs(quoteDiff)}%
              </>
            ) : (
              "–"
            )}
          </td>
          <td
            className={clientDiff && clientDiff >= 0 ? "positive" : "negative"}
          >
            {clientDiff !== null ? (
              <>
                {clientDiff > 0 ? (
                  <TrendingUpIcon width="16" height="16" />
                ) : (
                  <TrendingDownIcon width="16" height="16" />
                )}
                {Math.abs(clientDiff)}%
              </>
            ) : (
              "–"
            )}
          </td>
          <td
            className={driverDiff && driverDiff >= 0 ? "positive" : "negative"}
          >
            {driverDiff !== null ? (
              <>
                {driverDiff > 0 ? (
                  <TrendingUpIcon width="16" height="16" />
                ) : (
                  <TrendingDownIcon width="16" height="16" />
                )}
                {Math.abs(driverDiff)}%
              </>
            ) : (
              "–"
            )}
          </td>
          <td
            className={
              refundsDiff && refundsDiff >= 0 ? "positive" : "negative"
            }
          >
            {refundsDiff !== null ? (
              <>
                {refundsDiff > 0 ? (
                  <TrendingUpIcon width="16" height="16" />
                ) : (
                  <TrendingDownIcon width="16" height="16" />
                )}
                {Math.abs(refundsDiff)}%
              </>
            ) : (
              "–"
            )}
          </td>
          <td
            className={
              reviewsDiff && reviewsDiff >= 0 ? "positive" : "negative"
            }
          >
            {reviewsDiff !== null ? (
              <>
                {reviewsDiff > 0 ? (
                  <TrendingUpIcon width="16" height="16" />
                ) : (
                  <TrendingDownIcon width="16" height="16" />
                )}
                {Math.abs(reviewsDiff)}%
              </>
            ) : (
              "–"
            )}
          </td>
        </tr>,
      );
    });

    return rows;
  }, [reportData, compareData, reportYear, compareYear]);

  const selectedReportYear = useMemo(
    () => yearOptions?.find((option) => option?.value === reportYear),
    [yearOptions, reportYear],
  );

  const selectedCompareYear = useMemo(
    () => yearOptions?.find((option) => option?.value === compareYear),
    [yearOptions, compareYear],
  );

  const selectedFocusMonth = useMemo(
    () => monthOptions?.find((option) => option?.value === focusMonth),
    [monthOptions, focusMonth],
  );

  const handleReset = () => {
    setReportYear(currentYear);
    setCompareYear(currentYear - 1);
    setFocusMonth(currentMonth);
  };

  const handlePrevMonth = () => {
    setFocusMonth((prev) => (prev === 0 ? 11 : prev - 1));
  };

  const handleNextMonth = () => {
    setFocusMonth((prev) => {
      const nextMonth = prev === 11 ? 0 : prev + 1;
      const isCurrentYear = reportYear === currentYear;

      if (isCurrentYear && nextMonth > currentMonth) {
        return prev;
      }

      if (isCurrentYear && prev === 11) {
        return prev;
      }

      return nextMonth;
    });
  };

  return (
    <>
      <TopNav title={"Monthly reports"} />

      <div className="global-body">
        <div className="global-body-container">
          <SearchFilterBar
            filters={[
              {
                type: "select",
                key: "reportYear",
                label: "Reporting year",
                value: reportYear,
                onChange: (val) => setReportYear(val),
                options: yearOptions,
              },
              {
                type: "select",
                key: "compareYear",
                label: "Compare with",
                value: compareYear,
                onChange: (val) => setCompareYear(val),
                options: yearOptions,
              },
            ]}
            onReset={handleReset}
          >
            <div className="rpt-month-selector-wrapper">
              <label className="form-label">Focus month</label>
              <div className="month-selector">
                <button
                  className="month-nav-btn"
                  onClick={handlePrevMonth}
                  title="Previous month"
                >
                  ‹
                </button>
                <ReactSelect
                  value={selectedFocusMonth}
                  onChange={(opt) => setFocusMonth(opt.value)}
                  options={monthOptions}
                  styles={selectStyles}
                  isSearchable={false}
                  classNamePrefix="custom-select"
                  className="react-select-container focus-month-select"
                />
                <button
                  className="month-nav-btn"
                  onClick={handleNextMonth}
                  title="Next month"
                >
                  ›
                </button>
              </div>
            </div>
          </SearchFilterBar>
          <div className="rpt-comparison-section">
            <div className="rpt-comparison-header mb-16">
              <h2>
                {MONTHS[focusMonth]} {reportYear} vs {MONTHS[focusMonth]}{" "}
                {compareYear}
              </h2>
            </div>
            {bannerInfo && (
              <div className="rpt-info-banner mb-24">
                Comparing {bannerInfo.startDay}-{bannerInfo.endDay}{" "}
                {bannerInfo.month} in both {reportYear} and {compareYear}. The
                month is still in progress; differences show the same period in
                each year.
              </div>
            )}
            <div className="notif-kpi-section">
              <KPICardsGrid cards={statCards} compareYear={compareYear} />
            </div>
          </div>

          <div className="table-responsive">
            <table className="table monthly-comparison-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Period</th>
                  <th>Jobs posted</th>
                  <th>Unquoted jobs</th>
                  <th>Bookings</th>
                  <th>Booking conversion</th>
                  <th>Quotes</th>
                  <th>Client registrations</th>
                  <th>Driver registrations</th>
                  <th>Refunds</th>
                  <th>Reviews</th>
                </tr>
              </thead>
              <tbody>{tableRows}</tbody>
            </table>
          </div>

          <MonthlyTrendChart
            reportData={reportData}
            compareData={compareData}
            getMetricValue={getMetricValue}
            reportYear={reportYear}
            compareYear={compareYear}
            selectStyles={selectStyles}
            months={MONTHS}
          />
        </div>
      </div>
    </>
  );
};

RptMonthly.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigate(RptMonthly);
